import { can as canEffectivePermission } from "./rolePermissions";

const normalizeRoleKey = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_");

export const normalizeSystemRoleValue = (value) => {
  const token = normalizeRoleKey(value);
  if (!token) return "";
  if (
    token === "SUPERADMIN" ||
    token === "SUPER_ADMIN" ||
    token === "PLATFORMADMIN"
  )
    return "SUPER_ADMIN";
  if (
    token === "ADMIN" ||
    token === "SCHOOL_USER" ||
    token === "SCHOOLUSER" ||
    token === "SCHOOL_ADMIN"
  )
    return "ADMIN";
  if (token === "TEACHER") return "TEACHER";
  if (token === "PARENT") return "STUDENT";
  if (token === "STUDENT") return "STUDENT";
  if (normalizeManagementRoleValue(token)) return "ADMIN";
  return "";
};

export const normalizeManagementRoleValue = (value) => {
  const token = normalizeRoleKey(value);
  if (!token) return "";
  if (token === "DIRECTOR") return "DIRECTOR";
  if (
    token.includes("DIRECTOR") ||
    token.includes("FOUNDER") ||
    token.includes("FINANCE") ||
    token.includes("FINANCIAL")
  )
    return "DIRECTOR";
  if (
    token === "ACCOUNTANT" ||
    token === "ACCOUNTS" ||
    token === "ACCOUNTS_OFFICER"
  )
    return "ACCOUNTANT";
  if (
    token === "PRINCIPAL" ||
    token === "HEADMASTER" ||
    token === "HEAD_TEACHER"
  )
    return "PRINCIPAL";
  if (token === "VP" || token === "VICE_PRINCIPAL" || token === "VICEPRINCIPAL")
    return "VP";
  if (token.startsWith("VP_") || token.includes("VP_")) return "VP";
  if (
    token === "COORDINATOR" ||
    token === "TEAM_LEADER" ||
    token === "TEAMLEADER"
  )
    return "COORDINATOR";
  if (
    token === "RECEPTION" ||
    token === "RECEPTIONIST" ||
    token === "FRONT_DESK"
  )
    return "RECEPTION";
  return "";
};

export const normalizeManagementRoleKey = (value) => {
  const raw = String(value ?? "").trim();
  const normalized = normalizeManagementRoleValue(raw);
  if (normalized) return normalized.toLowerCase();
  return raw ? "unknown" : "";
};

export const normalizeUserRole = (user = {}) => {
  const managementFirst =
    user?.management_role ||
    user?.managementRole ||
    user?.management_role_type ||
    user?.managementRoleType ||
    "";
  const normalizedManagement = normalizeManagementRoleValue(managementFirst);
  if (normalizedManagement) return normalizedManagement;
  return "";
};

export const resolveSessionRoles = (session = {}) => {
  const rbac =
    session?.rbac && typeof session.rbac === "object" ? session.rbac : {};
  const user =
    (session?.user && typeof session.user === "object" && session.user) ||
    (session?.admin_data &&
      typeof session.admin_data === "object" &&
      session.admin_data) ||
    {};
  const explicitSystemRole =
    rbac.system_role || session.system_role || session.systemRole || "";
  const managementRoleRaw =
    rbac.management_role ||
    session.management_role ||
    session.managementRole ||
    "";
  const managementRole = normalizeManagementRoleValue(managementRoleRaw);
  const systemRole = normalizeSystemRoleValue(explicitSystemRole);

  return { systemRole, managementRole, managementRoleRaw, user };
};

export const resolveDashboardRoute = ({ systemRole, managementRole }) => {
  if (systemRole === "SUPER_ADMIN") return "/superadmin";
  const roleKey = String(managementRole || "")
    .trim()
    .toLowerCase();
  if (roleKey === "vp") return "/dashboard/vp";
  if (roleKey && roleKey !== "unknown") return `/dashboard/${roleKey}`;
  if (systemRole === "ADMIN") return "/dashboard/management";
  if (systemRole === "TEACHER") return "/teacher";
  if (systemRole === "STUDENT") return "/student";
  return "/login";
};

export const MANAGEMENT_ROLE_KEYS = [
  "director",
  "principal",
  "vp",
  "coordinator",
  "reception",
  "accountant",
];

export const ROLE_LABELS = {
  superadmin: "Super Admin",
  management: "Management",
  unknown: "Unknown Role",
  director: "Director",
  principal: "Principal",
  vp: "Vice Principal",
  coordinator: "Coordinator",
  reception: "Reception",
  accountant: "Accountant",
  teacher: "Teacher",
  parent: "Student",
  student: "Student",
};

export const ROLE_SCOPES = {
  superadmin: "platform",
  management: "school",
  director: "school",
  principal: "school",
  vp: "division",
  coordinator: "class",
  reception: "school",
  accountant: "school",
  teacher: "class",
  parent: "student",
  student: "student",
};

export const resolveManagementRoleKey = (user = {}) => {
  const normalized = normalizeUserRole(user);
  if (normalized) return normalized.toLowerCase();
  const directToken =
    user?.management_role ||
    user?.managementRole ||
    user?.management_role_type ||
    user?.managementRoleType ||
    "";
  return normalizeManagementRoleKey(directToken || "");
};

export const isManagementRole = (role) => MANAGEMENT_ROLE_KEYS.includes(role);

export const can = (permissionKey, source = {}) => {
  if (!permissionKey) return false;
  const rolePermissions =
    source?.rolePermissions ||
    source?.me?.rolePermissions ||
    source?.user?.rolePermissions ||
    source?.session?.rolePermissions ||
    null;
  return canEffectivePermission(permissionKey, rolePermissions);
};

export const canCloseComplaints = (role) => ["principal", "vp"].includes(role);

export const canApproveMessaging = (role) =>
  ["director", "principal", "vp"].includes(role);

export const resolveRoleLabel = (role) => ROLE_LABELS[role] || "User";
